import React from 'react';
import { TrendingUp, Minus, TrendingDown, Zap } from 'lucide-react';
import type { SeasonalityMonth } from '../../../types/acquisitionAnalysis';

interface SeasonalityComparisonProps {
  data: SeasonalityMonth[];
}

export const SeasonalityComparison: React.FC<SeasonalityComparisonProps> = ({ data }) => {
  const formatCurrency = (amount: number | null) => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'Strong':
        return <Zap className="w-4 h-4 text-emerald-400" />;
      case 'Growing':
        return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'Stable':
        return <Minus className="w-4 h-4 text-blue-400" />;
      case 'Declining':
        return <TrendingDown className="w-4 h-4 text-red-400" />;
      default:
        return null;
    }
  };

  const getTrendBadge = (trend: string) => {
    const configs: Record<string, string> = {
      Strong: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      Growing: 'bg-green-500/20 text-green-300 border-green-500/30',
      Stable: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      Declining: 'bg-red-500/20 text-red-300 border-red-500/30',
      'No Data': 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    };

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
          configs[trend] || configs.Stable
        }`}
      >
        {getTrendIcon(trend)}
        {trend}
      </span>
    );
  };

  // Get FY labels from data (fallback to generic labels). Hide the FY3
  // column entirely when no data is present — empty placeholder columns
  // are confusing and clutter the table for the common 2-FY case.
  const fy1Label = data[0]?.fy1Label || 'FY1';
  const fy2Label = data[0]?.fy2Label || 'FY2';
  const fy3Label = data[0]?.fy3Label || 'FY3';
  const showFy3 = data.some((row) => row.fy3 !== null && row.fy3 !== undefined && row.fy3 !== 0);
  const yearCount = (showFy3 ? 3 : 2);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">
          Seasonality Comparison ({yearCount}-Year View)
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Month
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  {fy1Label}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  {fy2Label}
                </th>
                {showFy3 && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    {fy3Label}
                  </th>
                )}
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                  {yearCount}-Year Trend
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {data.map((row, index) => (
                <tr key={index} className="hover:bg-slate-700/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-white">{row.month}</td>
                  <td className="px-4 py-3 text-sm text-right text-slate-300">
                    {formatCurrency(row.fy1)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-slate-300">
                    {formatCurrency(row.fy2)}
                  </td>
                  {showFy3 && (
                    <td className="px-4 py-3 text-sm text-right font-semibold text-indigo-400">
                      {formatCurrency(row.fy3)}
                    </td>
                  )}
                  <td className="px-4 py-3 text-center">{getTrendBadge(row.trend)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
        <p className="text-sm text-emerald-300">
          <strong>Pattern:</strong>{' '}
          {data.filter((m) => m.trend === 'Growing' || m.trend === 'Strong').length} of{' '}
          {data.length} months show positive growth trends. Peak months are typically{' '}
          {data
            .filter((m) => m.trend === 'Strong')
            .map((m) => m.month)
            .join(', ') || 'consistent throughout the year'}
          .
        </p>
      </div>
    </div>
  );
};
